/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import {
    SalesDashboardFilters,
    KpiSummary,
    SalesAttribution,
    TopItems,
    TopItemsParams,
    Adjustments,
    Leaderboard,
    LeaderboardParams
} from 'model/salesDashboard';
import {
    SALES_DASHBOARD_ERROR,
    SALES_DASHBOARD_CLEAR_ERROR,
    SALES_DASHBOARD_KPI_SUMMARY_FETCH_START,
    SALES_DASHBOARD_KPI_SUMMARY_FETCH_SUCCESS,
    SALES_DASHBOARD_SALES_ATTRIBUTION_FETCH_START,
    SALES_DASHBOARD_SALES_ATTRIBUTION_FETCH_SUCCESS,
    SALES_DASHBOARD_TOP_ITEMS_FETCH_START,
    SALES_DASHBOARD_TOP_ITEMS_FETCH_SUCCESS,
    SALES_DASHBOARD_ADJUSTMENTS_FETCH_START,
    SALES_DASHBOARD_ADJUSTMENTS_FETCH_SUCCESS,
    SALES_DASHBOARD_LEADERBOARD_FETCH_START,
    SALES_DASHBOARD_LEADERBOARD_FETCH_SUCCESS,
    SALES_DASHBOARD_UPDATE_FILTERS,
    SalesDashboardAction
} from './SalesDashboardActionsTypes';
import {
    convertToKpiSummary,
    convertToSalesAttribution,
    convertToTopItems,
    convertToAdjustments,
    convertToLeaderboard,
    toSalesDashboardQueryDto
} from './SalesDashboardConverter';
import { KpiSummaryDto, SalesAttributionDto, TopItemsDto, AdjustmentsDto, LeaderboardDto } from './SalesDashboardDtos';

const salesDashboardUrl = (orgId: number): string => `/organizations/${orgId}/sales_dashboard`;

// Extract meaningful error message from API error response
const extractErrorMessage = (apiError: any): string => {
    if (apiError?.response?.data) {
        const { data } = apiError.response;

        if (typeof data === 'object' && !data.message && !data.error && !data.errors) {
            const fieldNames = Object.keys(data);
            if (fieldNames.length > 0) {
                const firstField = fieldNames[0];
                const fieldError = data[firstField];
                if (Array.isArray(fieldError) && fieldError.length > 0) {
                    return `${firstField} ${fieldError[0]}`;
                }
                if (typeof fieldError === 'string') {
                    return `${firstField} ${fieldError}`;
                }
            }
        }

        if (data.errors) {
            if (typeof data.errors === 'object') {
                const errorKeys = Object.keys(data.errors);
                if (errorKeys.length > 0) {
                    const firstKey = errorKeys[0];
                    const firstError = data.errors[firstKey];
                    if (Array.isArray(firstError) && firstError.length > 0) {
                        return `${firstKey} ${firstError[0]}`;
                    }
                    return `${firstKey} ${firstError}`;
                }
            }
            if (typeof data.errors === 'string') {
                return data.errors;
            }
        }

        if (data.message) {
            return data.message;
        }

        if (data.error) {
            return data.error;
        }
    }

    return apiError?.message || 'An unexpected error occurred';
};

const error = (err: string): SalesDashboardAction => ({
    type: SALES_DASHBOARD_ERROR,
    payload: { error: err }
});

export const clearSalesDashboardError = (): SalesDashboardAction => ({
    type: SALES_DASHBOARD_CLEAR_ERROR
});

export const updateFilters = (filters: Partial<SalesDashboardFilters>): SalesDashboardAction => ({
    type: SALES_DASHBOARD_UPDATE_FILTERS,
    payload: { filters }
});

export const fetchKpiSummaryThunk = (
    organizationId: number,
    filters: SalesDashboardFilters
): ThunkAction<Promise<KpiSummary>, RootState, null, SalesDashboardAction> => {
    return async dispatch => {
        dispatch({ type: SALES_DASHBOARD_KPI_SUMMARY_FETCH_START });

        try {
            const { data }: AxiosResponse<KpiSummaryDto> = await ApiClient.get(
                `${salesDashboardUrl(organizationId)}/kpi_summary`,
                { params: toSalesDashboardQueryDto(filters) }
            );

            const kpiSummary = convertToKpiSummary(data);

            dispatch({
                type: SALES_DASHBOARD_KPI_SUMMARY_FETCH_SUCCESS,
                payload: { kpiSummary }
            });

            return kpiSummary;
        } catch (apiError) {
            dispatch(error(extractErrorMessage(apiError)));
            throw apiError;
        }
    };
};

export const fetchSalesAttributionThunk = (
    organizationId: number,
    filters: SalesDashboardFilters
): ThunkAction<Promise<SalesAttribution>, RootState, null, SalesDashboardAction> => {
    return async dispatch => {
        dispatch({ type: SALES_DASHBOARD_SALES_ATTRIBUTION_FETCH_START });

        try {
            const { data }: AxiosResponse<SalesAttributionDto> = await ApiClient.get(
                `${salesDashboardUrl(organizationId)}/sales_attribution`,
                { params: toSalesDashboardQueryDto(filters) }
            );

            const salesAttribution = convertToSalesAttribution(data);

            dispatch({
                type: SALES_DASHBOARD_SALES_ATTRIBUTION_FETCH_SUCCESS,
                payload: { salesAttribution }
            });

            return salesAttribution;
        } catch (apiError) {
            dispatch(error(extractErrorMessage(apiError)));
            throw apiError;
        }
    };
};

export const fetchTopItemsThunk = (
    organizationId: number,
    params: TopItemsParams
): ThunkAction<Promise<TopItems>, RootState, null, SalesDashboardAction> => {
    return async dispatch => {
        dispatch({ type: SALES_DASHBOARD_TOP_ITEMS_FETCH_START });

        try {
            const { type, page, perPage, query, ...filters } = params;
            const { data }: AxiosResponse<TopItemsDto> = await ApiClient.get(
                `${salesDashboardUrl(organizationId)}/top_items`,
                {
                    params: {
                        ...toSalesDashboardQueryDto(filters),
                        ...(type !== undefined && { type }),
                        ...(page !== undefined && { page }),
                        ...(perPage !== undefined && { per_page: perPage }),
                        ...(query !== undefined && { query })
                    }
                }
            );

            const topItems = convertToTopItems(data);

            dispatch({
                type: SALES_DASHBOARD_TOP_ITEMS_FETCH_SUCCESS,
                payload: { topItems }
            });

            return topItems;
        } catch (apiError) {
            dispatch(error(extractErrorMessage(apiError)));
            throw apiError;
        }
    };
};

export const fetchAdjustmentsThunk = (
    organizationId: number,
    filters: SalesDashboardFilters
): ThunkAction<Promise<Adjustments>, RootState, null, SalesDashboardAction> => {
    return async dispatch => {
        dispatch({ type: SALES_DASHBOARD_ADJUSTMENTS_FETCH_START });

        try {
            const { data }: AxiosResponse<AdjustmentsDto> = await ApiClient.get(
                `${salesDashboardUrl(organizationId)}/adjustments`,
                { params: toSalesDashboardQueryDto(filters) }
            );

            const adjustments = convertToAdjustments(data);

            dispatch({
                type: SALES_DASHBOARD_ADJUSTMENTS_FETCH_SUCCESS,
                payload: { adjustments }
            });

            return adjustments;
        } catch (apiError) {
            dispatch(error(extractErrorMessage(apiError)));
            throw apiError;
        }
    };
};

export const fetchLeaderboardThunk = (
    organizationId: number,
    params: LeaderboardParams
): ThunkAction<Promise<Leaderboard>, RootState, null, SalesDashboardAction> => {
    return async dispatch => {
        dispatch({ type: SALES_DASHBOARD_LEADERBOARD_FETCH_START });

        try {
            const { sortBy, ...filters } = params;
            const { data }: AxiosResponse<LeaderboardDto> = await ApiClient.get(
                `${salesDashboardUrl(organizationId)}/leaderboard`,
                {
                    params: {
                        ...toSalesDashboardQueryDto(filters),
                        ...(sortBy !== undefined && { sort_by: sortBy })
                    }
                }
            );

            const leaderboard = convertToLeaderboard(data);

            dispatch({
                type: SALES_DASHBOARD_LEADERBOARD_FETCH_SUCCESS,
                payload: { leaderboard }
            });

            return leaderboard;
        } catch (apiError) {
            dispatch(error(extractErrorMessage(apiError)));
            throw apiError;
        }
    };
};
